import React from "react"
import { TouchableOpacity, View } from "react-native"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { isWebMobile } from "@common/utils/mobileUtils"
import { APP_URL } from "@data/constants/apiConstants"

/**
 * Comment rejoindre le live.
 *
 * Ce n'est pas un écran de panne, c'est une étape du parcours — et c'est tout
 * ce qui compte ici. Zoom ne dit jamais que quelque chose ne va pas : il pose
 * « Participer à la réunion », propose le bon chemin, laisse l'autre
 * disponible, et on avance. On lit une étape, pas un incident.
 *
 * Deux versions s'y sont cassé les dents. L'une transformait la recommandation
 * en alerte — triangle, « ce navigateur risque de couper ton live » — et faisait
 * peur pour rien. L'autre la transformait en procédure : trois étapes numérotées
 * qu'on lit comme une réparation à faire avant d'avoir le droit d'entrer.
 *
 * Il reste donc : le but en titre, une raison en une ligne, une action réelle,
 * et la sortie en lien. Le seul bouton est celui qui fait vraiment quelque
 * chose — aucun navigateur ne peut en ouvrir un autre, il n'existe pas de
 * bouton « ouvrir Chrome » honnête.
 */

const CHROME_URL = "https://www.google.com/intl/fr_fr/chrome/"

/**
 * L'adresse à taper dans Chrome, sans son protocole.
 *
 * Tirée d'`APP_URL` et non d'une table locale : la table que ce fichier
 * portait oubliait l'environnement de développement, et disait donc à un élève
 * en local d'aller sur `app.masteur.com`. Le fichier de constantes prévient
 * déjà qu'il faut le tenir synchronisé avec `app.config.js` — ça faisait un
 * troisième endroit à ne pas oublier.
 */
const APP_DOMAIN = APP_URL.replace(/^https?:\/\//, "")

const BrowserChoiceScreen = ({ width, onContinueHere, onOpenStore }) => {
    const onMobile = isWebMobile()

    return (
        <View
            style={{
                width: Math.min(width, 440),
                alignSelf: "center",
                paddingHorizontal: 24,
                paddingTop: 56,
            }}
        >
            <TextM
                wrap
                fontSize={25}
                fontWeight={"Bold"}
                color={theme.colors.black}
                style={{ textAlign: "center", letterSpacing: -0.4 }}
            >
                Rejoindre ton live
            </TextM>

            {/* Une raison, pas un avertissement. */}
            <TextM
                wrap
                fontSize={14.5}
                color={theme.colors.grey}
                style={{ textAlign: "center", marginTop: 12, lineHeight: 21 }}
            >
                {onMobile
                    ? "Le live est plus stable dans l'application que dans le navigateur du téléphone."
                    : "Le live est plus stable sur Google Chrome."}
            </TextM>

            <TouchableOpacity
                onPress={
                    onMobile
                        ? onOpenStore
                        : () => {
                            // Dans un nouvel onglet : sinon l'élève perd la page
                            // du live, et n'a plus qu'un bouton « retour » pour
                            // la retrouver.
                            window.open(CHROME_URL, "_blank")
                        }
                }
                style={{
                    marginTop: 28,
                    backgroundColor: theme.colors.primary,
                    borderRadius: 10,
                    paddingVertical: 15,
                    alignItems: "center",
                }}
            >
                <TextM color={theme.colors.white} fontSize={15} fontWeight={"Bold"}>
                    {onMobile ? "Ouvrir l'application Masteur" : "Installer Google Chrome"}
                </TextM>
            </TouchableOpacity>

            {/* Pour qui l'a déjà : la seule chose à savoir, en une phrase. */}
            <TextM
                wrap
                fontSize={13}
                color={theme.colors.light}
                style={{ textAlign: "center", marginTop: 14, lineHeight: 19 }}
            >
                {onMobile ? (
                    "Tu l'as déjà ? Ouvre-la, ton live t'y attend."
                ) : (
                    <>
                        {"Tu l'as déjà ? Ouvre Chrome et va sur "}
                        <TextM color={theme.colors.primary} fontSize={13} fontWeight={"Bold"}>
                            {APP_DOMAIN}
                        </TextM>
                    </>
                )}
            </TextM>

            {/* La sortie : disponible, jamais proposée. */}
            <TouchableOpacity
                onPress={onContinueHere}
                hitSlop={12}
                style={{ marginTop: 34, alignSelf: "center", paddingVertical: 6 }}
            >
                <TextM
                    color={theme.colors.light}
                    fontSize={13.5}
                    style={{ textDecorationLine: "underline" }}
                >
                    Continuer sur ce navigateur
                </TextM>
            </TouchableOpacity>
        </View>
    )
}

export default BrowserChoiceScreen
